/**
 * System administrators — who else can open this console.
 *
 * A system administrator reaches every customer's data, so this list is
 * short and only the platform owner can change it; everyone else who opens
 * the page sees the list and a note saying so. Adding someone is by email:
 * they get a message saying what changed, and at their next sign-in they
 * set up Google Authenticator before anything opens. Removing someone
 * ends their sessions within the hour.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ResetMfaButton } from './ResetMfaButton';
import { call, isCallFailure } from '../../lib/callable';
import { useAuth } from '../../auth/useAuth';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import own from './Console.module.css';
import page from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import { EMAIL_MAX, PERSON_NAME_MAX } from '../../types/fieldLimits';

export interface SysAdminRow {
  uid: string;
  email: string;
  displayName: string;
  status: 'active' | 'revoked';
  grantedAt: string | null;
  enrolled: boolean;
}

interface ListResult {
  sysAdmins: SysAdminRow[];
  /** Whether the caller may grant and revoke: only the platform owner. */
  canManage: boolean;
}

interface SetResult {
  success: boolean;
  notified: boolean;
  created: boolean;
}

export function SysAdminsPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['sysadmins'],
    retry: false,
    queryFn: () => call<Record<string, never>, ListResult>('sysAdminListSysAdmins', {}),
  });

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const grant = useMutation({
    mutationFn: (input: { email: string; displayName: string }) =>
      call<{ email: string; displayName?: string; grant: true }, SetResult>('sysAdminSetSysAdmin', {
        email: input.email,
        ...(input.displayName ? { displayName: input.displayName } : {}),
        grant: true,
      }),
    onSuccess: (res, input) => {
      setNote(
        `${input.email} is now a system administrator.` +
        (res.notified ? ' They have been emailed.' : ' The email could not be sent; tell them yourself.') +
        ' Google Authenticator is required at their next sign-in.',
      );
      setEmail('');
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['sysadmins'] });
    },
  });

  const revoke = useMutation({
    mutationFn: (row: SysAdminRow) =>
      call<{ targetUid: string; grant: false }, SetResult>('sysAdminSetSysAdmin', { targetUid: row.uid, grant: false }),
    onSuccess: (_res, row) => {
      setNote(`${row.email} is no longer a system administrator. Their sessions end within the hour.`);
      void queryClient.invalidateQueries({ queryKey: ['sysadmins'] });
    },
  });

  if (list.isLoading) return <Spinner label="Loading system administrators" />;
  if (list.error) {
    return (
      <PageState title="Could not load system administrators" tone="alert">
        {isCallFailure(list.error) ? list.error.message : 'The list did not come back. Reload the page to try again.'}
      </PageState>
    );
  }

  const rows = list.data?.sysAdmins ?? [];
  const active = rows.filter((r) => r.status === 'active');
  const canManage = list.data?.canManage === true;
  const pending = grant.isPending || revoke.isPending;
  const failure = grant.error ?? revoke.error;

  function submit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value || !value.includes('@')) return;
    setNote(null);
    grant.mutate({ email: value, displayName: name.trim() });
  }

  return (
    <>
      <h1 className={own.title}>System administrators</h1>
      <p className={own.subtitle}>
        Everyone who can open this console and reach every organization on the system.
        Each one must use Google Authenticator, on the console and on the main site.
        {canManage
          ? ' Only you can add or remove them.'
          : ' Only the platform owner can add or remove them.'}
      </p>

      {note && (
        <div className={`${page.notice} ${page.noticeInfo}`} role="status">{note}</div>
      )}
      {failure && (
        <div className={own.error} role="alert">
          {isCallFailure(failure) ? failure.message : 'That did not go through.'}
        </div>
      )}

      {canManage && (
        <form onSubmit={submit} className={own.panel} style={{ padding: 'var(--sp-5)', maxWidth: 620, marginBottom: 'var(--sp-5)' }}>
          <h2 style={{ fontSize: 'var(--fs-lg)', margin: '0 0 var(--sp-3)' }}>Add a system administrator</h2>
          <div className={form.field} style={{ marginBottom: 'var(--sp-4)' }}>
            <label className={form.label} htmlFor="sysadmin-email">Email address</label>
            <input
              id="sysadmin-email"
              type="email"
              autoComplete="off"
              maxLength={EMAIL_MAX}
              className={form.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby="sysadmin-email-help"
              disabled={pending}
              required
            />
            <p className={form.hint} id="sysadmin-email-help">
              An account is created for a new address. They sign in with an emailed link at
              console.standingdocs.com and set up Google Authenticator before anything opens.
            </p>
          </div>
          <div className={form.field} style={{ marginBottom: 'var(--sp-4)' }}>
            <label className={form.label} htmlFor="sysadmin-name">Name (optional)</label>
            <input
              id="sysadmin-name"
              maxLength={PERSON_NAME_MAX}
              className={form.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={pending}
            />
          </div>
          <button type="submit" className={own.googleBtn} style={{ width: 'auto', padding: '9px 16px' }} disabled={pending}>
            {grant.isPending ? 'Adding…' : 'Make them a system administrator'}
          </button>
        </form>
      )}

      <div className={own.panel}>
        {active.length === 0 ? (
          <PageState title="No system administrators recorded">
            Add one with the form above. They sign in with Google and set up Google
            Authenticator before the console opens for them.
          </PageState>
        ) : (
          <table className={own.table}>
            <caption>{active.length} system {active.length === 1 ? 'administrator' : 'administrators'}</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Google Authenticator</th>
                <th scope="col">Since</th>
                {canManage && <th scope="col"><span className="vh">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {active.map((row) => {
                const self = row.uid === session?.uid;
                return (
                  <tr key={row.uid}>
                    <td>{row.displayName || <span style={{ color: 'var(--faint)' }}>—</span>}{self && <span style={{ color: 'var(--muted)' }}> (you)</span>}</td>
                    <td style={{ color: 'var(--ink-2)' }}>{row.email}</td>
                    <td>
                      <span className={`${own.pill} ${row.enrolled ? own.pillActive : own.pillDraft}`}>
                        {row.enrolled ? 'Set up' : 'Not yet'}
                      </span>
                    </td>
                    <td className="tnum">{row.grantedAt ? new Date(row.grantedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : '—'}</td>
                    {canManage && (
                      <td>
                        {!self && (
                          <span style={{ display: 'inline-flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                            {row.enrolled && row.status === 'active' && (
                              <ResetMfaButton targetUid={row.uid} who={row.email} onDone={setNote} invalidate={[['sysadmins']]} buttonClassName={own.plainBtn} />
                            )}
                            <button
                              type="button"
                              className={own.plainBtn}
                              disabled={pending}
                              onClick={() => { setNote(null); revoke.mutate(row); }}
                              aria-label={`Remove ${row.email} as a system administrator`}
                            >
                              Remove
                            </button>
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
